import { defaultTo, isNil } from 'lodash';
import moment from 'moment';
import { EnrollmentFieldOptions } from 'enums/Enrollment/EnrollmentFieldOptions';
import Address from 'interfaces/Address';
import {
    API_ENDPOINT,
    getAuthHeaders,
    getLocalTimeHeader,
    getSearchParamsString,
} from './api';
import { handleResponseConversionToJSON } from './utility';
import { DateTime } from 'luxon';

const urlGetPlansByZipCodeEnrollYear = '/Plan/GetPlansByZipCodeEnrollYear';
const urlGetProviderDetails = '/Provider/GetProviderDetails';
const urlGetEnrollmentDocuments = '/Enrollment/GetEnrollmentDocuments';
const urlGetPaperApplicationPDF = '/PaperApplication/GetPaperApplicationPDF';
const urlGetCountyByZipcode = '/Plan/GetCountyByZipcode';
const urlSubmitEnrollment = '/Enrollment/SubmitEnrollment';
const urlSavePendingApplication = '/Enrollment/SavePendingApplication';
const urlGetMemberOSBEligiblity = '/Benefits/GetMemberOSBEligibility';
const urlSubmitOSBEnrollment = '/Benefits/SubmitOSBEnrollment';
const urlGetOSBEnrollmentDocuments = '/Benefits/DownloadOSBEnrollmentDocument';
const urlUploadEnrollmentDocument = '/Enrollment/UploadEnrollmentDocuments';
const urlDeleteEnrollmentDocument = '/Enrollment/DeleteEnrollmentDocument';
const urlGetLeadDataById = '/Enrollment/GetLeadDataById';
const urlUpdateMBIDetails = '/Enrollment/UpdateMBIDetails';

const urlGetPendingApplication = '/Member/GetPendingApplication';
const urlApprovePendingApplicaiton = '/Member/ApprovePendingApplication';
const urlRejectPendingApplicaiton = '/Member/RejectPendingApplication';
const urlGetAgentInfoFromPendingApplication =
    '/Member/GetAgentInfoFromPendingApplication';
const urlGetPendingApplicationStatus = '/Member/GetPendingApplicationStatus';

const CACHE_ENROLLMENT = 'submit-enrollment';

const uploadEnrollmentDocuments = async (
    authToken: string,
    document,
    applicationId,
    fileType
) => {
    const headers = getAuthHeaders(authToken);

    const formData = new FormData();
    formData.append('document', document);

    const resp = await fetch(`${API_ENDPOINT}${urlUploadEnrollmentDocument}`, {
        method: 'POST',
        headers: {
            ApplicationID: applicationId,
            FileTypeID: fileType,
            Authorization: headers['Authorization'],
        },
        body: formData,
    });

    return resp;
};

const submitBenefitEnrollment = async (
    authToken: string,
    benefitEnrollment
) => {
    const authHeaders = getAuthHeaders(authToken);

    const headers: HeadersInit = { ...getLocalTimeHeader(), ...authHeaders };

    const currentTimestamp = moment();

    const url = `${API_ENDPOINT}${urlSubmitOSBEnrollment}`;
    const response = fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
            ...benefitEnrollment,
            submittedTimestamp: currentTimestamp,
        }),
    }).then((response) => response.json());
    return response;
};

const getMemberOSBEligibility = async (
    authToken: string,
    firstName,
    lastName,
    dateOfBirth,
    medicareId,
    memberId
) => {
    const authHeaders = getAuthHeaders(authToken);

    const headers: HeadersInit = { ...getLocalTimeHeader(), ...authHeaders };

    const url = `${API_ENDPOINT}${urlGetMemberOSBEligiblity}?${getSearchParamsString(
        {
            firstName,
            lastName,
            dateOfBirth,
            medicareId,
            memberId,
        }
    )}`;
    const response = fetch(url, {
        method: 'GET',
        headers,
    }).then((response) => response.json());
    return response;
};

const getPlansByZipCodeEnrollYear = async (
    authToken: string,
    zipcode,
    coverageYear,
    cacheVersionId
) => {
    const headers = getAuthHeaders(authToken);

    const url = `${API_ENDPOINT}${urlGetPlansByZipCodeEnrollYear}?${getSearchParamsString(
        {
            zipcode,
            coverageYear,
        }
    )}`;

    const cache = await caches.open(`${CACHE_ENROLLMENT}-${cacheVersionId}`);
    const cachedResponse = await cache.match(url);

    if (isNil(cachedResponse)) {
        const response = fetch(url, {
            method: 'GET',
            headers,
        }).then((response) => {
            if (response.ok) {
                cache.put(url, response.clone());
            }
            return response.json();
        });
        return response;
    }

    return cachedResponse.json();
};

const getProviderDetails = async (
    authToken: string,
    Zipcode = '',
    PBP = '',
    CoverageYear = '',
    ContractCode = '',
    SearchTerm = '',
    PageIndex = 1,
    PageSize = 3,
    ProviderCategory = ''
) => {
    const headers = getAuthHeaders(authToken);
    const url = `${API_ENDPOINT}${urlGetProviderDetails}?${getSearchParamsString(
        {
            Zipcode,
            PBP,
            CoverageYear,
            ContractCode,
            SearchTerm,
            PageIndex,
            PageSize,
            ProviderCategory,
        }
    )}`;
    const response = fetch(url, {
        method: 'GET',
        headers,
    }).then(handleResponseConversionToJSON);
    return response;
};
const getEnrollmentDocumentsUrl = (applicationId, documentId, documentType) => {
    return `${API_ENDPOINT}${urlGetEnrollmentDocuments}?${getSearchParamsString(
        {
            applicationId,
            documentId,
            documentType,
        }
    )}`;
};
const getOSBEnrollmentDocumentsUrl = (applicationId, documentId) => {
    return `${API_ENDPOINT}${urlGetOSBEnrollmentDocuments}?${getSearchParamsString(
        {
            applicationId,
            documentId,
        }
    )}`;
};
const getPaperApplicationPdfUrl = (applicationId) => {
    return `${API_ENDPOINT}${urlGetPaperApplicationPDF}?${getSearchParamsString(
        {
            applicationId,
        }
    )}`;
};
const getCountyByZipcode = async (authToken: string, zipcode) => {
    const authHeaders = getAuthHeaders(authToken);

    const headers = { ...authHeaders, ...getLocalTimeHeader() };
    const url = `${API_ENDPOINT}${urlGetCountyByZipcode}?${getSearchParamsString(
        {
            zipcode,
        }
    )}`;
    const response = fetch(url, {
        method: 'GET',
        headers,
    }).then((response) => response.json());
    return response;
};

const getMemberEnrollStatus = async (
    authToken: string,
    medicareID,
    ProspectiveEffectiveDate,
    PBPCode,
    ContractCode,
    MemberDOB,
    AgentID,
    StateCode
) => {
    const headers = getAuthHeaders(authToken);
    const url = `api/Enrollment/GetMemberEnrollStatus?HICNumber=${medicareID}&${getSearchParamsString(
        {
            ProspectiveEffectiveDate,
            PBPCode,
            ContractCode,
            MemberDOB,
            AgentID,
            StateCode,
        }
    )}`;
    const response = fetch(url, {
        method: 'GET',
        headers,
    }).then((response) => response.json());
    return response;
};

const getCMSEligibilityDetail = async (
    authToken: string,
    medicareID,
    lastName,
    dateOfBirth
) => {
    const headers = getAuthHeaders(authToken);

    const queryParamStr = new URLSearchParams({
        HICNumber: defaultTo(medicareID, ''),
        lastName: defaultTo(lastName, ''),
        birthDate: defaultTo(dateOfBirth, ''),
    }).toString();

    const url = `api/Enrollment/GetCMSEligibilityDetail?${queryParamStr}`;
    const response = fetch(url, {
        method: 'GET',
        headers,
    }).then((response) => response.json());
    return response;
};

const getLeadDataById = async (authToken: string, leadID, applicationID) => {
    const authHeaders = getAuthHeaders(authToken);
    const headers = {
        ...authHeaders,
        ApplicationID: applicationID,
    };
    const url = `${API_ENDPOINT}${urlGetLeadDataById}?${getSearchParamsString({
        leadID,
    })}`;
    const response = fetch(url, {
        method: 'GET',
        headers,
    }).then((response) => (response.status !== 200 ? null : response.json()));
    return response;
};

const deleteEnrollmentDocument = async (
    authToken: string,
    documentID,
    applicationID
) => {
    const authHeaders = getAuthHeaders(authToken);
    const headers = {
        ...authHeaders,
        DocumentID: documentID,
        ApplicationID: applicationID,
    };
    const url = `${API_ENDPOINT}${urlDeleteEnrollmentDocument}`;
    const response = fetch(url, {
        method: 'POST',
        headers,
    });
    return response;
};

const resetEnrollmentCache = () => caches.delete(CACHE_ENROLLMENT);

const submitEnrollmentApplication = async (authToken: string, enrollment) => {
    const authHeaders = getAuthHeaders(authToken);
    const headers = { ...authHeaders, Accept: 'application/json' };
    const url = `${API_ENDPOINT}${urlSubmitEnrollment}`;

    const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(enrollment),
    });
    const data = await response.json();
    return {
        status: response.status,
        data,
    };
};
const savePendingApplication = async (authToken: string, enrollment) => {
    const authHeaders = getAuthHeaders(authToken);
    const headers = { ...authHeaders, Accept: 'application/json' };
    const url = `${API_ENDPOINT}${urlSavePendingApplication}`;

    const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(enrollment),
    });
    const data = await response.json();
    return {
        status: response.status,
        data,
    };
};

const getCallRecording = async (authToken: string, applicationId, roomId) => {
    const headers = getAuthHeaders(authToken);
    return await fetch(
        `api/enrollment/getCallRecording?${getSearchParamsString({
            applicationId,
            roomId,
        })}`,
        {
            method: 'GET',
            headers,
        }
    );
};

const getCallRecordingMetadata = async (authToken: string, applicationId) => {
    const headers = getAuthHeaders(authToken);
    return await fetch(
        `api/enrollment/getCallRecordingMetadata?${getSearchParamsString({
            applicationId,
        })}`,
        {
            method: 'GET',
            headers,
        }
    );
};

const getEnrollmentScripts = async (authToken: string) => {
    const headers = getAuthHeaders(authToken);
    const response = await fetch(`api/enrollment/getEnrollmentScripts`, {
        method: 'GET',
        headers,
    });

    if (response.ok) {
        return await response.json();
    }
    return { scripts: [], urlExpiration: new Date() };
};

const validateAddress = async (
    authToken: string,
    address: Address,
    verifyZipCode: boolean
) => {
    const headers = getAuthHeaders(authToken);
    return await fetch(
        `api/enrollment/ValidateAddress?${getSearchParamsString({
            ...address,
            verifyZipCode,
        })}`,
        {
            method: 'GET',
            headers,
        }
    );
};

const updateMBIDetails = async (
    authToken: string,
    mbi,
    dob,
    cmsFieldsMatchStatus,
    applicationId,
    notificationType
) => {
    const headers = getAuthHeaders(authToken);
    const url = `${API_ENDPOINT}${urlUpdateMBIDetails}?${getSearchParamsString({
        mbi,
        dob,
        cmsFieldsMatchStatus,
        applicationId,
        notificationType,
    })}`;

    const response = await fetch(url, {
        method: 'POST',
        headers,
    }).then((response) => response.json());

    return response;
};

const sendApprovalForPendingApplication = async (
    clientId,
    reviewBeneficiarySignature,
    reviewBeneficiarySignatureDate
) => {
    const url = `${API_ENDPOINT}${urlApprovePendingApplicaiton}?${getSearchParamsString(
        {
            clientId,
            reviewBeneficiarySignature,
            reviewBeneficiarySignatureDate,
        }
    )}`;

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getLocalTimeHeader(),
        },
    }).then((response) => response.json());

    return response;
};
const sendRejectionForPendingApplication = async (clientId) => {
    const url = `${API_ENDPOINT}${urlRejectPendingApplicaiton}?${getSearchParamsString(
        {
            clientId,
        }
    )}`;

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...getLocalTimeHeader(),
        },
    }).then((response) => response.json());

    return response;
};
const getPendingApplication = async (clientId) => {
    const url = `${API_ENDPOINT}${urlGetPendingApplication}?${getSearchParamsString(
        {
            clientId,
        }
    )}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    }).then((response) => response.json());

    return response;
};

const getEnrollmentFieldOptions = async (
    authToken: string,
    optionsField: EnrollmentFieldOptions
) => {
    const headers = getAuthHeaders(authToken);
    const response = await fetch(
        `api/enrollment/GetEnrollmentFieldOptions?${getSearchParamsString({
            optionsField,
        })}`,
        {
            method: 'GET',
            headers,
        }
    ).then((response) => response.json());
    return response;
};

const getAgentInfoFromPendingApplication = async (userId) => {
    const url = `${API_ENDPOINT}${urlGetAgentInfoFromPendingApplication}?${getSearchParamsString(
        {
            userId,
        }
    )}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
    }).then((response) => response.json());

    return response;
};

const getPendingApplicationStatus = async (clientId: string) => {
    const url = `${API_ENDPOINT}${urlGetPendingApplicationStatus}?${getSearchParamsString(
        {
            clientId,
        }
    )}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            ...getLocalTimeHeader(),
        },
    }).then((response) => response.json());

    return response;
};

export {
    getMemberOSBEligibility,
    getPlansByZipCodeEnrollYear,
    getProviderDetails,
    getEnrollmentDocumentsUrl,
    getPaperApplicationPdfUrl,
    getCountyByZipcode,
    deleteEnrollmentDocument,
    resetEnrollmentCache,
    submitEnrollmentApplication,
    savePendingApplication,
    submitBenefitEnrollment,
    getMemberEnrollStatus,
    getCMSEligibilityDetail,
    getOSBEnrollmentDocumentsUrl,
    urlUploadEnrollmentDocument,
    urlDeleteEnrollmentDocument,
    uploadEnrollmentDocuments,
    getLeadDataById,
    getCallRecording,
    getCallRecordingMetadata,
    getEnrollmentScripts,
    validateAddress,
    updateMBIDetails,
    getEnrollmentFieldOptions,
    getPendingApplication,
    sendRejectionForPendingApplication,
    sendApprovalForPendingApplication,
    getAgentInfoFromPendingApplication,
    getPendingApplicationStatus,
};
